// Moved by `git mv` from
// `src/hooks/mutation/integration/ehr-launch/useMutateUpdateIntegrationEhrLaunch.ts`,
// which survives as a delegate — so `git log --follow` on THIS path stops at the
// move and the pre-move history is `git log --follow` on the source path. Its
// repo-root suite moved with it, relayered, and that one DOES trace.
//
// THE STORE TETHER THE RECORD FILED THIS UNDER WAS TWO DIFFERENT THINGS, and
// only one of them was ever a blocker. The `@/store` import here was a `type
// Dispatch` for one `dispatch.analyticsScribe.trackIntegrationUpdated` call —
// the Wave 1 · P2 severance, not a slice read — so it crosses as a PORT and the
// bus call stays in the delegate, unmoved and uncounted against this change.
//
// TOAST: DELEGATE BODY -> META (tier T1). Both halves of the success toast were
// literal English at the pre-carve source, so `meta` carries them verbatim and
// no copy resolution crosses. The error path logged twice and toasted nothing;
// that is transcribed, and no `errorToast` was invented for symmetry.
//
// TWO DELTAS, disclosed, and both are the ones already recorded on
// `useMutateDeleteIntegrationEhrLaunch` when it took this tier:
//   (a) ORDER — the cache-level toast handler runs BEFORE `options.onSuccess`,
//       so the toast now precedes the two invalidations where it used to follow
//       them. Nothing reads the toast, and the invalidations are awaited by the
//       same promise the caller awaits.
//   (b) REHYDRATION — `meta` survives dehydration where a closure did not.
//       Theoretical here: the app dehydrates queries plus PAUSED mutations only.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { logger } from '@heidi/runtime/logger';
import type { EHRProvider } from '@heidi/schema/ml-scribe';

import { ehrMutationKeys, ehrQueryKeys } from '../../contracts/queryKeys';
import type { UpdateUserEHRLaunchProfileV2Request } from '../../contracts/types';
import { updateIntegrationEhrLaunch } from '../api/launch-profile';
import { EHR_LAUNCH_PROFILE_TOASTS } from './ehrLaunchProfileToasts';

/**
 * The backend's error envelope, declared structurally for the ONE field the log
 * line reads.
 *
 * `ErrorResponse` is the app's `@/types` alias and is the only type this hook
 * imported that is NOT already in `./ehr/contracts` — because nothing else
 * behind this door needs it. Aliasing the whole envelope into `/contracts` to
 * read `detail.msg` would put a shared app shape behind a door that has no other
 * use for it; the two-line shape below is the honest width of the dependency.
 */
type BackendErrorEnvelope = { detail?: { msg?: string } };

type Params = {
  /**
   * GET_USER_EHR now lives in ehr contracts; the delegate still passes userEhrQueryKey.
   */
  userEhrQueryKey: readonly unknown[];
  /**
   * Fired after both invalidations settle. The app delegate dispatches
   * `analyticsScribe.trackIntegrationUpdated` from here; a package `/data` zone
   * neither imports the bus nor names the event.
   */
  onIntegrationUpdated?: (provider: EHRProvider) => void;
};

/**
 * Updates one EHR launch profile
 * (`PATCH /ehr-launch/onboarding/user-ehr-launch-profile/{provider}`).
 *
 * BOTH INVALIDATIONS ARE AWAITED TOGETHER, as before: the profile list and the
 * user's EHR record both change, and the sole caller
 * (`useAutoCreateEhrProfile`) reads `isPending` off the mutation, so settling
 * one before the other would flip its spinner off with half the UI stale.
 */
export function useMutateUpdateIntegrationEhrLaunch({
  userEhrQueryKey,
  onIntegrationUpdated,
}: Params) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ehrMutationKeys.updateIntegrationEhrLaunch(),
    mutationFn: async (payload: {
      request: UpdateUserEHRLaunchProfileV2Request;
      provider: EHRProvider;
    }) => {
      return await updateIntegrationEhrLaunch(payload.request, payload.provider);
    },
    onSuccess: async (_, payload) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: userEhrQueryKey }),
        queryClient.invalidateQueries({
          queryKey: ehrQueryKeys.userEhrLaunchProfilesPrefix(),
        }),
      ]);

      onIntegrationUpdated?.(payload.provider);
    },
    onError: (error) => {
      const errorResponse = (error as AxiosError).response?.data as
        | BackendErrorEnvelope
        | undefined;

      // TWO LINES, THE SECOND OF WHICH CAN READ `[INT] undefined`. Transcribed:
      // the pre-move body concatenated an optional chain into a template, so an
      // envelope without a `detail.msg` logged exactly that. Guarding it here
      // would be a quiet behaviour change inside a move.
      logger.error('[INT] Failed to connect integration');
      logger.error('[INT] ' + errorResponse?.detail?.msg);
    },
    meta: EHR_LAUNCH_PROFILE_TOASTS.updateIntegrationEhrLaunch,
  });
}
